import type { MarcasClassificacao, RevendedorVerificado, SnapshotBuyBox } from './types'

export type BuyBoxStatus = {
  atualizando: boolean
  rescaneando: boolean
  ultima_atualizacao: string | null
  ultimo_rescan_catalogo: string | null
  ultimo_erro: string | null
}

/**
 * Chama o servico_buybox (ver specs/CONTRATO_API_BUYBOX.md). Same-origin — em produção o
 * dev decide se roda no mesmo domínio do fiesta-bi (proxy) ou em porta própria atrás do nginx,
 * igual à decisão já registrada para o servico_score.
 */
export class BuyBoxApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'BuyBoxApiError'
    this.status = status
  }
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  let resp: Response
  try {
    resp = await fetch(path, { credentials: 'include', ...init })
  } catch {
    throw new Error(
      'Serviço de Buy Box inacessível. Se acabámos de activar, confirma o proxy e o processo fiesta-buybox.',
    )
  }
  if (!resp.ok) {
    const corpo = (await resp.json().catch(() => null)) as {
      erro?: string
      detail?: string | { msg?: string }
      message?: string
    } | null
    const detail =
      typeof corpo?.detail === 'string'
        ? corpo.detail
        : corpo?.detail && typeof corpo.detail === 'object'
          ? corpo.detail.msg
          : undefined
    throw new BuyBoxApiError(
      resp.status,
      corpo?.erro ||
        detail ||
        corpo?.message ||
        `Falha na chamada ao serviço de Buy Box (HTTP ${resp.status}).`,
    )
  }
  return resp.json() as Promise<T>
}

export function fetchBuyBoxSnapshot(contaId: string): Promise<SnapshotBuyBox> {
  return api<SnapshotBuyBox>(`/api/buybox/dados?conta=${contaId}`)
}

export type BuyBoxAnalise = {
  ok: boolean
  texto?: string
  leitura?: string
  modelo?: string
  fonte?: string
  gerado_em?: string
  cached?: boolean
  erro?: string
  error?: string
  message?: string
}

export async function fetchBuyBoxAnalise(contaId: string): Promise<BuyBoxAnalise> {
  const resp = await fetch(`/api/bridge/buybox/analise?conta=${encodeURIComponent(contaId)}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  const data = (await resp.json().catch(() => ({}))) as BuyBoxAnalise
  if (!resp.ok && !data.ok) {
    return {
      ok: false,
      erro:
        data.erro ||
        data.message ||
        data.error ||
        `Falha na leitura IA (HTTP ${resp.status}).`,
    }
  }
  return data
}

export function fetchBuyBoxStatus(contaId: string): Promise<BuyBoxStatus> {
  return api<BuyBoxStatus>(`/api/buybox/status?conta=${contaId}`)
}

export function dispararAtualizacaoStatus(contaId: string): Promise<{ ok: true }> {
  return api(`/api/buybox/atualizar?conta=${contaId}`, { method: 'POST' })
}

/** Mantido pra uso futuro (ex.: cron) — o botão "Atualizar" da tela dispara só
 * dispararAtualizacaoStatus, que agora rescaneia o catálogo E checa o status num só passo
 * (ver servico.py `checar_status`). */
export function dispararRescanCatalogo(contaId: string): Promise<{ ok: true }> {
  return api(`/api/buybox/rescan-catalogo?conta=${contaId}`, { method: 'POST' })
}

export function fetchMarcas(): Promise<MarcasClassificacao> {
  return api<MarcasClassificacao>('/api/buybox/marcas')
}

export function adicionarMarca(marcaRaw: string, classificacao: 'exclusiva' | 'grupo'): Promise<unknown> {
  return api('/api/buybox/marcas', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ marca_raw: marcaRaw, classificacao }),
  })
}

export function removerMarca(marcaRaw: string): Promise<{ ok: true }> {
  return api(`/api/buybox/marcas/${encodeURIComponent(marcaRaw)}`, { method: 'DELETE' })
}

export function fetchRevendedoresVerificados(): Promise<RevendedorVerificado[]> {
  return api<RevendedorVerificado[]>('/api/buybox/revendedores-verificados')
}

export function marcarRevendedorVerificado(dados: {
  seller_id: string
  nome_vendedor: string
  cnpj: string
  nota_fiscal: string
  observacao?: string
}): Promise<RevendedorVerificado> {
  return api<RevendedorVerificado>('/api/buybox/revendedores-verificados', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dados),
  })
}
